import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Cloud, Wind, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { cn } from '@/lib/utils';
import { StatusIndicator } from './StatusIndicator';
import type { AirQualityMetric } from '@/types/energy';
import { AIR_QUALITY_COLORS, AIR_QUALITY_THRESHOLDS } from '@/types/energy';

type MetricType = 'co2' | 'voc' | 'pm25';

interface AirQualityGaugeProps {
  /** null/undefined = no sensor data (prod: Atmocube not installed) → N/A state */
  data?: AirQualityMetric | null;
  type: MetricType;
  /** Whether live demo mode is active */
  isLive?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'wide';
  /** Additional CSS classes */
  className?: string;
}

const METRIC_CONFIG: Record<MetricType, {
  label: string;
  icon: typeof Cloud;
  unitLabel: string;
  minValue: number;
  maxValue: number;
  infoContent: React.ReactNode;
}> = {
  co2: {
    label: 'CO₂',
    icon: Cloud,
    unitLabel: 'ppm',
    minValue: 400,
    maxValue: 2000,
    infoContent: (
      <div className="space-y-1">
        <p className="font-semibold">CO₂ Concentration</p>
        <p>Indoor carbon dioxide levels measured in parts per million (ppm). Indicates ventilation effectiveness and air freshness.</p>
        <p className="text-muted-foreground">Good: &lt;800ppm, Moderate: 800-1000ppm, Poor: 1000-1500ppm, Very Poor: &gt;1500ppm</p>
      </div>
    ),
  },
  voc: {
    label: 'VOC Index',
    icon: Wind,
    unitLabel: 'index',
    minValue: 0,
    maxValue: 500,
    infoContent: (
      <div className="space-y-1">
        <p className="font-semibold">VOC Index</p>
        <p>Volatile Organic Compounds index measuring air quality from chemicals, paints, and cleaning products. Lower is better.</p>
        <p className="text-muted-foreground">Good: &lt;150, Moderate: 150-250, Poor: 250-400, Very Poor: &gt;400</p>
      </div>
    ),
  },
  pm25: {
    label: 'PM2.5',
    icon: Sparkles,
    unitLabel: 'μg/m³',
    minValue: 0,
    maxValue: 100,
    infoContent: (
      <div className="space-y-1">
        <p className="font-semibold">PM2.5 Particulates</p>
        <p>Fine particulate matter (2.5 micrometers or smaller) from dust, smoke, and pollution. Can affect respiratory health.</p>
        <p className="text-muted-foreground">Good: &lt;12μg/m³, Moderate: 12-35μg/m³, Poor: 35-55μg/m³, Very Poor: &gt;55μg/m³</p>
      </div>
    ),
  },
};

/**
 * Reusable air quality gauge component
 * Supports CO2, VOC, and PM2.5 with configurable sizes
 */
export function AirQualityGauge({
  data,
  type,
  isLive = false,
  size = 'sm',
  className,
}: AirQualityGaugeProps) {
  const config = METRIC_CONFIG[type];
  const Icon = config.icon;

  // No sensor data (prod: air-quality hardware not installed, or a reading
  // is missing) — render an honest N/A card instead of substituting the
  // scale minimum and presenting it as a live measurement.
  if (!data || !Number.isFinite(data.current)) {
    return (
      <Card className={cn('flex flex-col h-full overflow-hidden relative', className)}>
        <div className="absolute bottom-1.5 right-2 z-10">
          <InfoTooltip content={config.infoContent} />
        </div>
        <CardContent className="flex-1 flex flex-col p-2 gap-1.5">
          <div className="flex items-center gap-1 shrink-0">
            <Icon className="h-3 w-3 text-[#8FB560]" />
            <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              {config.label}
            </h3>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-0.5">
            <span className="text-2xl font-bold text-muted-foreground">N/A</span>
            <span className="text-[9px] text-muted-foreground">No sensor data</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const color = AIR_QUALITY_COLORS[data.status];

  // Validate current value: ensure finite number before calculations
  const safeCurrent = data.current;

  // Normalize value to percentage
  const normalizedPercent = Math.min(
    100,
    Math.max(0, ((safeCurrent - config.minValue) / (config.maxValue - config.minValue)) * 100)
  );

  // Size-based configuration - all use p-2 for vertical alignment of headers
  const sizeConfig = {
    sm: { gaugeHeight: 70, gaugeWidth: 70, fontSize: '14px', headerSize: 'text-[10px]', unitSize: 'text-[10px]', padding: 'p-2' },
    md: { gaugeHeight: 90, gaugeWidth: 90, fontSize: '18px', headerSize: 'text-[10px]', unitSize: 'text-[10px]', padding: 'p-2' },
    lg: { gaugeHeight: 150, gaugeWidth: 150, fontSize: '30px', headerSize: 'text-[10px]', unitSize: 'text-[10px]', padding: 'p-2' },
    wide: { gaugeHeight: 80, gaugeWidth: 80, fontSize: '16px', headerSize: 'text-[10px]', unitSize: 'text-[10px]', padding: 'p-2' },
  }[size];

  const options: ApexOptions = {
    chart: {
      type: 'radialBar',
      sparkline: { enabled: true },
      animations: { enabled: !isLive },
    },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: {
          size: '55%',
        },
        track: {
          background: 'rgba(255,255,255,0.1)',
          strokeWidth: '100%',
        },
        dataLabels: {
          show: true,
          name: { show: false },
          value: {
            show: true,
            fontSize: sizeConfig.fontSize,
            fontWeight: 700,
            color: color,
            offsetY: 5,
            formatter: () => type === 'pm25' ? safeCurrent.toFixed(1) : `${Math.round(safeCurrent)}`,
          },
        },
      },
    },
    colors: [color],
    stroke: {
      lineCap: 'round',
    },
  };

  const series = [normalizedPercent];

  // Wide layout - horizontal bar chart showing value against health thresholds
  if (size === 'wide') {
    // Get thresholds for this metric type
    const thresholdKey = type === 'voc' ? 'vocIndex' : type;
    const thresholds = AIR_QUALITY_THRESHOLDS[thresholdKey];

    // Calculate bar width percentage (capped at max)
    const barPercent = Math.min(100, (safeCurrent / config.maxValue) * 100);

    // Calculate threshold positions as percentages
    const goodPercent = (thresholds.good / config.maxValue) * 100;
    const moderatePercent = (thresholds.moderate / config.maxValue) * 100;
    const poorPercent = (thresholds.poor / config.maxValue) * 100;

    return (
      <Card className={cn('flex flex-col h-full overflow-hidden relative', className)}>
        <div className="absolute bottom-1.5 right-2 z-10">
          <InfoTooltip content={config.infoContent} />
        </div>
        <CardContent className="flex-1 flex flex-col p-2 gap-1.5">
          {/* Header row with label and current value */}
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-1">
              <Icon className="h-3 w-3 text-[#8FB560]" />
              <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                {config.label}
              </h3>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-bold" style={{ color }}>
                {type === 'pm25' ? safeCurrent.toFixed(1) : Math.round(safeCurrent)}
              </span>
              <span className="text-[9px] text-muted-foreground">{config.unitLabel}</span>
            </div>
          </div>

          {/* Bar chart with thresholds */}
          <div className="flex-1 flex flex-col justify-center gap-1 px-4">
            {/* Legend positioned above bar - each label centered over its zone */}
            <div className="relative h-4 mb-1">
              {/* Good label - centered over green zone */}
              <div
                className="absolute text-[10px] text-muted-foreground flex items-center gap-1 whitespace-nowrap"
                style={{ left: `${goodPercent / 2}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-2 h-2 rounded-full bg-[#8FB560]" />
                <span>Good</span>
              </div>

              {/* Moderate label - centered over yellow zone */}
              <div
                className="absolute text-[10px] text-muted-foreground flex items-center gap-1 whitespace-nowrap"
                style={{ left: `${(goodPercent + moderatePercent) / 2}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
                <span>Moderate</span>
              </div>

              {/* Poor label - centered over orange zone */}
              <div
                className="absolute text-[10px] text-muted-foreground flex items-center gap-1 whitespace-nowrap"
                style={{ left: `${(moderatePercent + poorPercent) / 2}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span>Poor</span>
              </div>

              {/* Very Poor label - centered over red zone */}
              <div
                className="absolute text-[10px] text-muted-foreground flex items-center gap-1 whitespace-nowrap"
                style={{ left: `${(poorPercent + 100) / 2}%`, transform: 'translateX(-50%)' }}
              >
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span>Very Poor</span>
              </div>
            </div>

            {/* Main bar container */}
            <div className="relative h-6 bg-muted/20 rounded-full overflow-hidden">
              {/* Threshold zone backgrounds */}
              <div
                className="absolute inset-y-0 left-0 bg-[#8FB560]/20"
                style={{ width: `${goodPercent}%` }}
              />
              <div
                className="absolute inset-y-0 bg-yellow-500/20"
                style={{ left: `${goodPercent}%`, width: `${moderatePercent - goodPercent}%` }}
              />
              <div
                className="absolute inset-y-0 bg-orange-500/20"
                style={{ left: `${moderatePercent}%`, width: `${poorPercent - moderatePercent}%` }}
              />
              <div
                className="absolute inset-y-0 right-0 bg-red-500/20"
                style={{ left: `${poorPercent}%` }}
              />

              {/* Current value bar */}
              <div
                className="absolute inset-y-1 left-1 rounded-full transition-all duration-500"
                style={{
                  width: `calc(${barPercent}% - 8px)`,
                  backgroundColor: color,
                  minWidth: '4px'
                }}
              />

              {/* Threshold markers */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-[#8FB560]"
                style={{ left: `${goodPercent}%` }}
                title={`Good: <${thresholds.good}`}
              />
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-yellow-500"
                style={{ left: `${moderatePercent}%` }}
                title={`Moderate: <${thresholds.moderate}`}
              />
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-orange-500"
                style={{ left: `${poorPercent}%` }}
                title={`Poor: <${thresholds.poor}`}
              />
            </div>

            {/* Min/Max scale below bar */}
            <div className="flex justify-between text-[9px] text-muted-foreground">
              <span>0</span>
              <span>{config.maxValue}</span>
            </div>
          </div>

          {/* Status indicator - with spacer to align with CO₂ panel */}
          <div className="flex flex-col items-center gap-0.5 shrink-0">
            <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Current Status</span>
            <StatusIndicator status={data.status} size="sm" />
          </div>
          <div className="h-3 shrink-0" />
        </CardContent>
      </Card>
    );
  }

  // Standard vertical layout (left-aligned header for CO₂)
  return (
    <Card className={cn('flex flex-col h-full overflow-hidden relative', className)}>
      <div className="absolute top-1.5 right-2 z-10">
        <InfoTooltip content={config.infoContent} />
      </div>
      <CardContent className={cn('flex-1 flex flex-col items-center justify-between gap-0', sizeConfig.padding)}>
        {/* Header - left aligned, green icon */}
        <div className="flex items-center gap-1 w-full">
          <Icon className="h-3.5 w-3.5 text-[#8FB560]" />
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            {config.label}
          </h3>
        </div>

        {/* Radial Gauge */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <Chart
            type="radialBar"
            options={options}
            series={series}
            height={sizeConfig.gaugeHeight}
            width={sizeConfig.gaugeWidth}
          />
          <span className={cn('text-muted-foreground -mt-1', sizeConfig.unitSize)}>{config.unitLabel}</span>
        </div>

        {/* Status - centered */}
        <div className="flex flex-col items-center gap-0.5 w-full">
          <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Current Status</span>
          <StatusIndicator status={data.status} size="sm" />
        </div>

        {/* Spacer row for vertical alignment with other panels */}
        <div className="h-3 shrink-0" />
      </CardContent>
    </Card>
  );
}
